import axios from "axios";
import { backend_url } from "@/constants";
import { Endpoints } from "./endpoints";
import type { JobStatus } from "@utils/jobs.ts";

const MS_TO_SECONDS = 1000;

/**
 * Polls job until status has finished (either failed or success) or timeout is reached.
 * @param jobId ID of the job to poll.
 * @param interval The interval to poll status endpoint (in seconds).
 * @param timeout The amount of seconds to timeout on.
 * @returns The status of the job.
 */
export async function pollJob(
  jobId: string,
  interval = 5,
  timeout = 3600
): Promise<JobStatus> {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const poll = async () => {
      // Get Job Status
      const statusResponse = await axios.get(
        `${backend_url}/${Endpoints.jobs.status}/${jobId}`,
        {
          withCredentials: true,
        }
      );

      if (statusResponse.status !== 200) {
        reject(new Error("Failed to get job status."));
        return "failed";
      }

      // Job has stopped running, return status.
      const status = statusResponse.data.content.status;
      if (status === "completed" || status === "failed") {
        resolve(status);
        return;
      }

      // Chek if we have timed-out
      if (Date.now() - startTime >= timeout * MS_TO_SECONDS) {
        reject(new Error("Polling timed out"));
        return;
      }

      setTimeout(poll, interval * MS_TO_SECONDS);
    };

    poll();
  });
}

/**
 * Returns the result of a job.
 * @param jobId ID of job whose result is retrieved.
 * @returns Request containing response.
 */
export const getJobResult = async (jobId: string) => {
  const jobResultResponse = await axios.get(
    `${backend_url}/${Endpoints.jobs.result}/${jobId}`,
    { withCredentials: true }
  );
  if (jobResultResponse.status !== 200) {
    console.error(`Could not retrieve the result of job: ${jobId}`);
  }
  return jobResultResponse.data.content;
};

export const pollUntilComplete = async (
  jobId: string,
  maxRetries = 20,
  delay = 1500
): Promise<JobStatus> => {
  let retries = 0;

  while (retries < maxRetries) {
    const status = await pollJob(jobId);
    console.log(
      `Polling job ${jobId}, attempt ${retries + 1}, status: ${status}`
    );

    if (status === "completed" || status === "failed") {
      return status;
    }

    await new Promise((resolve) => setTimeout(resolve, delay));
    retries++;
  }

  throw new Error("Job polling timed out");
};
